import type { GameId, PlayerPublic } from '@xox/shared'
import type { ReactNode } from 'react'
import { BOSS_THEME } from '../lib/bossTheme'
import { Avatar } from './Avatar'

interface Props {
  game: GameId
  bot: PlayerPublic
  /** Small caps line above the title ("Boss fight", "Hall of attempts"). */
  eyebrow: string
  title: string
  subtitle: string
  /** `h1` on a page of its own, `h2` inside another page. */
  heading?: 'h1' | 'h2'
  /** Everything under the name row: picker, chips, buttons. */
  children?: ReactNode
}

/** A house bot's dark gradient card: the glow, the avatar and the name row; callers add the rest. */
export function BossHero({ game, bot, eyebrow, title, subtitle, heading = 'h2', children }: Props) {
  const Heading = heading
  const theme = BOSS_THEME[game]
  return (
    <section
      className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-lg ring-1 ${theme.hero} ${theme.ring}`}
      aria-labelledby="boss-title"
    >
      <div
        className={`pointer-events-none absolute -top-10 -right-10 size-40 rounded-full opacity-15 blur-2xl ${theme.glow}`}
        aria-hidden
      />
      <div className="flex items-center gap-4">
        <Avatar
          id={bot.id}
          avatar={bot.avatar}
          color={bot.color}
          size="xl"
          className="ring-2 ring-white/30 shadow-[0_0_24px_rgba(255,255,255,.25)]"
        />
        <div className="min-w-0">
          <div className={`text-[11px] font-black uppercase tracking-[0.2em] ${theme.eyebrow}`}>
            {eyebrow}
          </div>
          <Heading id="boss-title" className="text-xl font-black leading-tight sm:text-2xl">
            {title}
          </Heading>
          <div className={`text-sm ${theme.sub}`}>{subtitle}</div>
        </div>
      </div>
      {children}
    </section>
  )
}
